import Radio, { RadioProps } from '@mui/material/Radio'
import { CSSProperties } from 'react'

type BluesiteRadioProps = RadioProps & {
  square?: boolean
}

const COLORS = {
  border: '#637381',
  borderSoft: '#B6C2D6',
  textStrong: '#163247',
  checked: '#C6016B',
  checkedSoft: 'rgba(198, 1, 107, 0.12)',
  disabled: '#C4CDD5',
}

const indicatorBaseStyle: CSSProperties = {
  width: 22,
  height: 22,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  border: `2px solid ${COLORS.border}`,
  backgroundColor: '#FFFFFF',
  transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, transform 160ms ease',
}

const CircleIcon = () => <span className="bluesite-radio-indicator" style={{ ...indicatorBaseStyle, borderRadius: '50%' }} />

const CircleCheckedIcon = () => (
  <span
    className="bluesite-radio-indicator bluesite-radio-indicator--checked"
    style={{
      ...indicatorBaseStyle,
      borderRadius: '50%',
      borderColor: COLORS.checked,
      boxShadow: `0 0 0 8px ${COLORS.checkedSoft}`,
    }}
  >
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: COLORS.checked,
      }}
    />
  </span>
)

const SquareIcon = () => (
  <span className="bluesite-radio-indicator" style={{ ...indicatorBaseStyle, borderRadius: 6, borderColor: COLORS.borderSoft }} />
)

const SquareCheckedIcon = () => (
  <span
    className="bluesite-radio-indicator bluesite-radio-indicator--checked"
    style={{
      ...indicatorBaseStyle,
      borderRadius: 6,
      borderColor: COLORS.checked,
      backgroundColor: COLORS.checked,
      boxShadow: `0 0 0 8px ${COLORS.checkedSoft}`,
    }}
  >
    <span
      style={{
        width: 6,
        height: 10,
        borderRight: '2px solid #FFFFFF',
        borderBottom: '2px solid #FFFFFF',
        transform: 'rotate(45deg) translate(-1px, -1px)',
      }}
    />
  </span>
)

const BluesiteRadio = ({ square = false, sx, ...props }: BluesiteRadioProps) => (
  <Radio
    disableRipple
    color="default"
    checkedIcon={square ? <SquareCheckedIcon /> : <CircleCheckedIcon />}
    icon={square ? <SquareIcon /> : <CircleIcon />}
    sx={[
      {
        padding: '6px',
        color: COLORS.textStrong,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:hover .bluesite-radio-indicator': {
          boxShadow: `0 0 0 8px ${COLORS.checkedSoft}`,
        },
        '&.Mui-focusVisible .bluesite-radio-indicator': {
          boxShadow: `0 0 0 8px ${COLORS.checkedSoft}`,
        },
        '&.Mui-disabled': {
          color: COLORS.disabled,
        },
        '&.Mui-disabled .bluesite-radio-indicator': {
          borderColor: COLORS.disabled,
          boxShadow: 'none',
          backgroundColor: '#FFFFFF',
          opacity: 0.7,
        },
        '&.Mui-disabled .bluesite-radio-indicator--checked': {
          backgroundColor: square ? COLORS.disabled : '#FFFFFF',
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...props}
  />
)

export default BluesiteRadio
